/**
 * How a landed hit feels, as opposed to what it does.
 *
 * Two of the three: **hitstop**, the brief freeze that makes a blow read as
 * contact rather than as two models passing through each other, and **camera
 * shake**, which is reserved for heavy hits so that it stays meaningful.
 * Knockback is the third, and it lives in `fight.js` with the rest of a blow's
 * physical consequences.
 *
 * Every number is in `feel.json`. That is the point of this module existing
 * separately: hit feel is tuned by feel, in small increments, dozens of times,
 * and none of that should be an edit to code.
 *
 * Pure — no three.js, no timers of its own. It is told a hit happened and fed
 * `dt`, and it answers two questions: is the fight frozen, and where is the
 * camera right now.
 */

import FEEL from './feel.json';

export { FEEL };

const STILL = Object.freeze({ x: 0, y: 0, z: 0 });

/**
 * Which column of the tuning table a hit reads from. A guard changes what a
 * blow feels like more than its strength does — everything lands softer
 * through a pair of forearms.
 */
export function feelKey({ strength, blocked } = {}) {
  if (blocked) return 'blocked';
  return strength === 'strong' ? 'strong' : 'normal';
}

export function createImpact(config = FEEL) {
  let frozen = 0;

  let shakeElapsed = 0;
  let shakeLeft = 0;
  let shakeAmplitude = 0;

  function offset() {
    if (shakeLeft <= 0 || shakeAmplitude <= 0) return STILL;
    // Squared falloff: a shake that fades linearly reads as a slow wobble,
    // where a hit should be a spike that is gone before you can look at it.
    const fall = (shakeLeft / config.shake.duration) ** 2;
    const t = shakeElapsed;
    const f = config.shake.frequency;
    // Three different frequencies, or the camera slides along one diagonal
    // instead of rattling.
    return {
      x: Math.sin(t * f) * shakeAmplitude * fall,
      y: Math.sin(t * f * 1.43 + 1.7) * shakeAmplitude * fall,
      z: Math.cos(t * f * 0.79) * shakeAmplitude * fall * 0.6,
    };
  }

  return {
    config,

    /** True while the fight is held still. Nothing should be stepped. */
    get frozen() {
      return frozen > 0;
    },
    get hitstop() {
      return Math.max(0, frozen);
    },
    get shaking() {
      return shakeLeft > 0;
    },
    /** Where to put the camera relative to the framing solution. */
    get offset() {
      return offset();
    },

    /**
     * Registers a landed blow.
     *
     * Both effects take the *larger* of what is running and what just arrived
     * rather than adding to it. A flurry should not compound into a half-second
     * freeze, and four jabs should not shake the camera off the screen — the
     * heaviest blow in an exchange is the one that should be felt.
     */
    hit(hit) {
      const key = feelKey(hit);

      frozen = Math.max(frozen, config.hitstop[key] ?? 0);

      const amplitude = config.shake[key] ?? 0;
      if (amplitude > 0) {
        // A bigger shake restarts the envelope; a smaller one arriving mid-shake
        // is swallowed by the one already running.
        if (amplitude >= shakeAmplitude || shakeLeft <= 0) {
          shakeAmplitude = amplitude;
          shakeLeft = config.shake.duration;
          shakeElapsed = 0;
        }
      }
      return key;
    },

    /**
     * Advanced with *real* time, never with the frozen fight's time — the
     * freeze has to be able to end itself, and the shake is presentation, so it
     * carries on rattling while the fighters are held still. That overlap is
     * most of why a hitstop reads as impact instead of as a dropped frame.
     */
    update(dt) {
      if (!(dt > 0)) return;
      if (frozen > 0) frozen -= dt;
      if (shakeLeft > 0) {
        shakeLeft -= dt;
        shakeElapsed += dt;
        if (shakeLeft <= 0) shakeAmplitude = 0;
      }
    },

    /** Drops everything. Used when the fight is reset out from under it. */
    clear() {
      frozen = 0;
      shakeLeft = 0;
      shakeElapsed = 0;
      shakeAmplitude = 0;
    },
  };
}
